/**
 * Normalizers for the Tyashin analytics-embed endpoints.
 *
 * The contract is YOM-EVERYWHERE-ARCHITECTURE.md §7 until T2 commits
 * `aapastech-backend/docs/analytics-embed-api.md`. These accept snake_case or
 * camelCase and a few plausible envelopes so a naming drift degrades to "no
 * data" instead of a white screen. Tighten them once the doc lands.
 */
import { MANAGED_ORG_STRATEGY, type EmbedSource, type ProjectAnalyticsStatus } from './types';
import type { TokenBundle } from './types';

type Obj = Record<string, unknown>;
const isObj = (v: unknown): v is Obj => Boolean(v) && typeof v === 'object' && !Array.isArray(v);
const str = (v: unknown): string | null => (typeof v === 'string' && v ? v : null);
const pick = (o: Obj, ...keys: string[]): unknown => {
  for (const k of keys) {
    if (o[k] !== undefined && o[k] !== null) return o[k];
  }
  return undefined;
};

/** Unwrap `{ success, data }` (axios gives us `res.data`, the API wraps again). */
export function unwrap(body: unknown): Obj {
  if (isObj(body) && isObj(body.data)) return body.data;
  return isObj(body) ? body : {};
}

export const DEFAULT_YOM_API = 'https://api.yourownmetrics.aapastech.com';

function toEpoch(v: unknown, fallbackMs: number): number {
  if (typeof v === 'number') return v < 1e12 ? v * 1000 : v;
  const s = str(v);
  if (s) {
    const t = Date.parse(s);
    if (!Number.isNaN(t)) return t;
  }
  return fallbackMs;
}

/** Defensive default when the backend omits expiry: 10 min (TTL ≤ 15 min). */
const DEFAULT_TTL_MS = 10 * 60_000;

function source(o: Obj, fallbackApi?: string): EmbedSource | null {
  const token = str(pick(o, 'token'));
  const project = str(pick(o, 'project', 'projectId', 'project_id', 'yomProjectId'));
  if (!token || !project) return null;
  return {
    token,
    project,
    api: str(pick(o, 'api', 'apiBase', 'api_base')) || fallbackApi || DEFAULT_YOM_API,
  };
}

/** GET /projects/:id/analytics/embed-token → bundle with one source: 'site'. */
export function normalizeProjectEmbedToken(body: unknown, now = Date.now()): TokenBundle {
  const o = unwrap(body);
  const src = source(o);
  if (!src) throw new Error('Analytics token response was missing a token or project');
  return {
    expiresAt: toEpoch(pick(o, 'expires_at', 'expiresAt'), now + DEFAULT_TTL_MS),
    sources: { site: src },
  };
}

/**
 * GET /admin/platform-insights/embed-token → bundle with sources 'app'
 * (T-APP) and 'sites' (T-SITES). Accepted shapes:
 *   { api, projects: { app: {token, project, expires_at} | null, sites }, missing: [] }  ← analytics-embed-api.md §4
 *   { app: {token, project, expires_at, api}, sites: {...} }
 *   { tokens: { 'T-APP': {...}, 'T-SITES': {...} } }
 *   { tokens: [{ key: 'T-APP', token, ... }, ...] }
 * Optional `funnels: { activation, trial_to_paid }` ride along as meta.
 */
export function normalizePlatformTokens(body: unknown, now = Date.now()): TokenBundle {
  const o = unwrap(body);
  const api = str(pick(o, 'api', 'apiBase'));
  const sources: Record<string, EmbedSource> = {};
  let expiresAt = Number.POSITIVE_INFINITY;

  const add = (key: string, raw: unknown) => {
    if (!isObj(raw)) return;
    const src = source(raw, api ?? undefined);
    if (!src) return;
    sources[key] = src;
    expiresAt = Math.min(
      expiresAt,
      toEpoch(pick(raw, 'expires_at', 'expiresAt', 'exp'), now + DEFAULT_TTL_MS)
    );
  };
  const keyOf = (k: string) => {
    const n = k.toLowerCase().replace(/[^a-z]/g, '');
    if (n === 'app' || n === 'tapp' || n === 'tyashinapp') return 'app';
    if (n === 'sites' || n === 'tsites' || n === 'tyashinsites' || n === 'tyashinsitesagg') {
      return 'sites';
    }
    return null;
  };

  const tokens = pick(o, 'projects', 'tokens') ?? o;
  if (Array.isArray(tokens)) {
    for (const t of tokens) {
      if (!isObj(t)) continue;
      const k = keyOf(String(pick(t, 'key', 'name', 'id') ?? ''));
      if (k) add(k, t);
    }
  } else if (isObj(tokens)) {
    for (const [k, v] of Object.entries(tokens)) {
      const key = keyOf(k);
      if (key) add(key, v);
    }
  }
  const meta: Record<string, string> = {};
  const missing = pick(o, 'missing');
  if (Array.isArray(missing) && missing.length) {
    meta.missing = missing.filter((m) => typeof m === 'string').join(', ');
  }
  if (!Object.keys(sources).length) {
    throw new Error(
      meta.missing
        ? `Platform insights are not configured yet (missing ${meta.missing}).`
        : 'Platform insights tokens were missing'
    );
  }

  const funnels = pick(o, 'funnels');
  if (isObj(funnels)) {
    for (const [k, v] of Object.entries(funnels)) {
      const s = str(v);
      if (s) meta[`funnel:${k.replace(/([A-Z])/g, '_$1').toLowerCase()}`] = s;
    }
  }
  const fallbackExpiry = toEpoch(pick(o, 'expires_at', 'expiresAt'), now + DEFAULT_TTL_MS);
  return {
    expiresAt: Number.isFinite(expiresAt) ? expiresAt : fallbackExpiry,
    sources,
    meta,
  };
}

/** GET /projects/:id/analytics/status */
export function normalizeAnalyticsStatus(body: unknown): ProjectAnalyticsStatus {
  const o = unwrap(body);
  const strategy = str(pick(o, 'strategy', 'provisioningStrategy', 'provisioning_strategy'));
  const provisionedRaw = pick(o, 'provisioned', 'isProvisioned', 'is_provisioned');
  const canClaim = pick(o, 'canClaim', 'can_claim');
  return {
    managedOrgs: Boolean(pick(o, 'managedOrgs', 'managed_orgs')),
    installed: Boolean(pick(o, 'installed')),
    enabled: Boolean(pick(o, 'enabled')),
    provisioned:
      typeof provisionedRaw === 'boolean' ? provisionedRaw : strategy === MANAGED_ORG_STRATEGY,
    strategy,
    claimed: Boolean(pick(o, 'claimed', 'isClaimed', 'claimed_at', 'claimedAt')),
    canClaim: typeof canClaim === 'boolean' ? canClaim : null,
    yomProjectId: str(pick(o, 'yomProjectId', 'yom_project_id')),
    dashboardUrl: str(pick(o, 'dashboardUrl', 'dashboard_url', 'yomUrl', 'yom_url')),
    collectingSince: str(
      pick(o, 'collectingSince', 'collecting_since', 'provisionedAt', 'provisioned_at')
    ),
    firstEventAt: str(pick(o, 'firstEventAt', 'first_event_at')),
    siteUrl: str(pick(o, 'siteUrl', 'site_url')),
    currency: str(pick(o, 'currency')),
  };
}

/** POST /projects/:id/analytics/claim-link → url */
export function normalizeClaimLink(body: unknown): string {
  const url = str(pick(unwrap(body), 'url', 'claimUrl', 'claim_url'));
  if (!url) throw new Error('Claim link response had no url');
  return url;
}
